import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges, OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges, ViewChild,
} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {Router} from '@angular/router';
import {DxTextBoxComponent} from 'devextreme-angular';
import DataSource from 'devextreme/data/data_source';
import ValidationEngine from 'devextreme/ui/validation_engine';
import * as moment from 'moment';
import {BehaviorSubject, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {User} from '../../../../../shared/models/fusion-auth';
import {PatientViewModel} from '../../../../../shared/models/ocl';
import {FusionAuthService} from '../../../../../shared/services/fusion-auth';
import {DOMService, ScreenService} from '../../../../../shared/services/general';
import {RecentPatientService} from '../../../../../shared/services/ocl';
import {PatientSearch, TransientPatient} from '../../../models/order-entry/patient';
import {PatientService} from '../../../services';
import * as Sentry from "@sentry/angular";


@Component({
  selector: 'app-patient-selector-dialog',
  templateUrl: './patient-selector-dialog.component.html',
  styleUrls: ['./patient-selector-dialog.component.scss'],
})
export class PatientSelectorDialogComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public popupVisible = false;
  @Input() public fromSearch = false;
  @Output() public closeEvent = new EventEmitter();

  private onDestroy$: Subject<void> = new Subject<void>();
  // @ts-ignore
  public searchedPatients: BehaviorSubject<DataSource> = new BehaviorSubject<DataSource>(null);
  @ViewChild('lastNameTextBox', {static: false}) public lastNameTextBox?: DxTextBoxComponent;
  public recentAvailable = false;
  public recentPatients?: PatientSearch[] | TransientPatient[];
  public patientDialogForm: FormGroup;
  public searched = false;
  public isLoading = false;
  public ssnMaskRule = {X: /[0-9]/};
  public today = Date.now();
  public firstDate = new Date('1/1/1900');
  public user?: User;
  public role?: string;
  public clientPopupVisible = false;
  public selectedPatient?: PatientSearch;
  public isPhone: boolean = false;
  public isPhoneLandscape: boolean = false;
  public isTablet: boolean = false;
  public clearButtonOptions: any;
  public newPatientButtonOptions: any;
  public closeButtonOptions: any;
  public buttons: any;
  public subtitle = '';

  constructor(
    private domService: DOMService,
    private router: Router,
    private fb: FormBuilder,
    public patientService: PatientService,
    public recentPatientService: RecentPatientService,
    private fusionAuthService: FusionAuthService,
    private screen: ScreenService,
    private renderer: Renderer2,
  ) {
    this.patientDialogForm = this.fb.group({
      lastName: ['', [Validators.required]],
      firstName: ['', [Validators.required]],
      dob: [''],
      ssn: [''],
    });

    this.clearButtonOptions = {
      text: 'Clear',
      onClick: () => {
        this.clear();
      },
    };

    this.newPatientButtonOptions = {
      text: 'New Patient',
      disabled: !this.searched,
      onClick: async () => {
        await this.newPatient();
      },
    };

    this.closeButtonOptions = {
      text: 'Close',
      onClick: () => {
        this.close();
      },
    };

    this.buttons = [
      {
        name: 'Clear',
      },
      {
        name: 'New Patient',
        disabled: true,
      },
      {
        name: 'Close',
      },
    ];
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private getRecentPatientsDropDownGridElement(): HTMLElement {
    return this.domService.getElementsByClassname('dx-overlay-wrapper dx-dropdowneditor-overlay dx-popup-wrapper').item(0) as HTMLElement;
  }

  public resizeDropDown() {
    const element = this.getRecentPatientsDropDownGridElement();

    if (element.style.display === 'none') {
      element.style.display = 'contents';
    }

    if (element) {
      if (this.isTablet || this.isPhoneLandscape) {
        this.renderer.setStyle(element, 'width', '450px');
        this.renderer.setStyle(element.firstChild, 'width', '450px');
        this.renderer.setStyle(element.firstChild?.firstChild, 'width', '450px');
      } else {
        this.renderer.setStyle(element, 'width', '580px');
        this.renderer.setStyle(element.firstChild, 'width', '580px');
        this.renderer.setStyle(element.firstChild?.firstChild, 'width', '580px');
      }
      this.renderer.setStyle(element, 'height', '38vh');
      this.renderer.setStyle(element.firstChild, 'height', '38vh');
      this.renderer.setStyle(element.firstChild?.firstChild, 'height', '38vh');
    }
  }

  public async onItemClick(e: any) {
    switch (e.itemData.name) {
      case 'Clear':
        this.clear();
        break;
      case 'New Patient':
        await this.newPatient();
        break;
      case 'Close':
        this.close();
        break;
    }
  }

  public ngOnInit(): void {
    this.isPhone = this.screen.sizes['screen-x-small'];
    this.isPhoneLandscape = this.screen.sizes['screen-small'];
    this.isTablet = this.screen.sizes['screen-tablet'];
    this.screen.changed.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.isPhone = this.screen.sizes['screen-x-small'];
      this.isPhoneLandscape = this.screen.sizes['screen-small'];
      this.isTablet = this.screen.sizes['screen-tablet'];
    });

    this.checkRecent();
    this.closeEvent.pipe(takeUntil(this.onDestroy$)).subscribe(patient => {
      if (patient) {
        this.recentPatientService.addRecentPatient(patient);
        const element = this.getRecentPatientsDropDownGridElement();
        if (element) {
          this.renderer.setStyle(element, 'display', 'none');
        }
        this.popupVisible = false;
      }
      this.clear();
    });

    this.waitByUser();
  }

  private waitByUser() {
    this.user = this.fusionAuthService.Profile;
    if (this.user) {
      this.role = this.user.registrations[0].roles[0];
    } else {
      setTimeout(() => {
        this.waitByUser();
      }, 1000);
    }
  }

  @HostListener('window:keyup', ['$event'])
  public keyEvent(event: KeyboardEvent) {
    try {
      if (event.key === 'Enter' && this.popupVisible) {
        if (!this.fromSearch) {
          ValidationEngine.validateGroup('patientDialog');
        }
        this.submitSearch();
      }
    } catch (e) {
      Sentry.captureException(e);
    }
  }

  private checkRecent() {
    this.recentPatients = this.recentPatientService.getRecentPatients();
    this.recentAvailable = this.recentPatients.length > 0;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.popupVisible && changes.popupVisible.currentValue) {
      this.renderer.setStyle(this.domService.getBody(), 'overflow', 'hidden');
      setTimeout(() => this.lastNameTextBox?.instance.instance().focus(), 1000);
      this.checkRecent();
    } else {
      this.renderer.setStyle(this.domService.getBody(), 'overflow', 'auto');
    }
  }

  public close(): void {
    this.patientDialogForm.reset();
    this.closeEvent.emit();
  }

  public rowClick(e: any) {
    if (e.rowType === 'data') {
      this.resultSelected(e.data);
    }
  }

  public clear(): void {
    ValidationEngine.resetGroup('patientDialog');
    this.patientDialogForm.reset();
    // @ts-ignore
    this.searchedPatients.next(null);
  }

  public resultSelected(patient: PatientSearch): void {
    if (patient) {
      this.selectedPatient = patient;
      this.role = this.user?.registrations[0].roles[0];
      this.subtitle = "";
      if (!patient.client.webAccess) {
        this.clientPopupVisible = true;
        //this.subtitle = "Patient client is not allowed to be accessed from the portal.";
        return;
      }
      if (this.role === 'Client Basic' || this.role === 'Client Admin') {
        const clientIds = this.user?.data.clients;
        this.patientService.checkPatientClients(patient.id as number, clientIds as number[])
          .pipe(takeUntil(this.onDestroy$))
          .subscribe(result => {
          this.clientPopupVisible = !result && !this.fromSearch;
          if (!this.clientPopupVisible) {
            this.closeForm(patient);
          }
        });
      } else {
        this.closeForm(patient);
      }
    }
  }

  private closeForm(patient: PatientSearch) {
    this.patientDialogForm.reset();
    // @ts-ignore
    this.searchedPatients.next(null);
    this.closeEvent.emit(patient);
  }

  public submitSearch() {
    if (this.patientDialogForm.valid || this.fromSearch) {
      const patient = {
        lastName: this.patientDialogForm.value.lastName,
        firstName: this.patientDialogForm.value.firstName,
        dob: this.patientDialogForm.value.dob,
        ssn: this.patientDialogForm.value.ssn,
      };
      this.isLoading = true;
      const role = this.user?.registrations[0].roles[0];
      const clientIds = role === 'Client Basic' || role === 'Client Admin' ? this.user?.data.clients : [];
      const physicianIds = role === 'Physician' ? this.user?.data.physicians : [];

      if (patient.dob !== null && patient.dob !== '' && patient.dob !== undefined) {
        patient.dob = moment(patient.dob).format('MM/DD/YYYY');
      } else {
        patient.dob = null;
      }

      this.searchedPatients.next(new DataSource({
        store: this.patientService.searchPatient(
          patient.firstName,
          patient.lastName,
          patient.dob,
          patient.ssn,
          clientIds as number[],
          physicianIds as number[],
          this.fromSearch,
          this.searchedPatients
        )
      }));

      this.searched = true;
      this.isLoading = false;

      this.buttons = [
        {
          name: 'Clear',
        },
        {
          name: 'New Patient',
          disabled: false,
        },
        {
          name: 'Close',
        },
      ];
    }
  }

  public async newPatient() {
    if (this.patientDialogForm.get('dob')?.value !== '') {
      this.patientDialogForm.get('dob')?.setValue(moment(this.patientDialogForm.get('dob')?.value).format('MM/DD/YYYY'));
    }
    await this.router.navigate(['secured/patients/form', {
      ...this.patientDialogForm.value,
      redirectToPath: this.router.url,
    }]);
  }

  public closeDialog(patient: PatientSearch): void {
    if (patient) {
      this.patientDialogForm.reset();
      // @ts-ignore
      this.searchedPatients.next(null);
      this.closeEvent.emit(patient);
      this.selectedPatient = patient;
    }
    this.clientPopupVisible = false;
  }

  public onBirthDateReady(e: any) {
    const element = e.element;
    if (e.component._pickerType === 'native') {
      const button = element.querySelector('.dx-texteditor-buttons-container');
      const input = element.querySelector('.dx-texteditor-input');
      this.renderer.setStyle(button, 'display', 'none');
      this.renderer.setStyle(input, 'padding-right', '5px');
    }
  }

  public dropdownButtonReady(e: any) {
    const button = e.element.querySelector('.dx-button-content');
    this.renderer.setStyle(button, 'padding-left', '5px');
    this.renderer.setStyle(button, 'padding-right', '5px');
  }
}
